/**
 * fetchCompParts — one-shot reads of a competition's subcollections.
 *
 * Thirteen places across eleven files wrote the same `Promise.all` of `getDocs`
 * calls followed by the same `snap.docs.map(d => ({ id: d.id, ...d.data() }))`.
 * They agreed on everything except which subcollections they wanted, so that is
 * the argument: a list of part names.
 *
 * The shapes are NOT uniform, and that is deliberate — each one is what its
 * consumers already expect:
 *
 * | part                   | shape                                        |
 * |------------------------|----------------------------------------------|
 * | `groups`               | `[{ id, ...data }]`                          |
 * | `sessions`             | `[{ id, ...data }]`                          |
 * | `registrations`        | `[{ id, ...data }]`                          |
 * | `attempts`             | `[{ id, ...data }]`                          |
 * | `results`              | `{ [groupId]: rankings[] }` — a MAP          |
 * | `resultDocs`           | `[{ id, ...data }]` — same collection, rows  |
 * | `rankings`             | `[{ id, ...data }]` — the precompute docs    |
 * | `registrationContacts` | `{ [uid]: data }` — a MAP                    |
 * | `participants`         | `[{ uid, ...data }]` — keyed `uid`, not `id` |
 *
 * `sorted` applies the app's two canonical orders (groups alphabetically per
 * R-CE-24, runs chronologically). It is opt-in rather than the default because
 * the PDF generators and the CSV export consume raw document order today, and
 * changing that would move visible rows under cover of a mechanical refactor.
 *
 * `tolerant` catches each part's read on its own and falls back to that part's
 * empty shape — for the surfaces (public calendar entry, admin analytics) where
 * a missing subcollection or a rules denial should hide one section rather than
 * fail the whole load.
 */
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { sortGroupsForDisplay } from "../competitionUtils";
import { sortSessionsByStartTime, PUBLIC_COMPETITION_STATUSES } from "@sendly/shared";

const rows = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));

// part name -> [reader, empty value, subcollection (defaults to the part name)]
const PARTS = {
  groups: [rows, []],
  sessions: [rows, []],
  registrations: [rows, []],
  attempts: [rows, []],
  rankings: [rows, []],
  participants: [(snap) => snap.docs.map((d) => ({ uid: d.id, ...d.data() })), []],
  results: [(snap) => {
    const out = {};
    snap.forEach((d) => { out[d.id] = d.data().rankings || []; });
    return out;
  }, {}],
  // The gym data export ships the whole result document onward — group id,
  // `rankings` array and every sibling field — so it reads the same
  // subcollection as `results`, in row shape. Folding the two into one part
  // would have to pick a winner, and every other caller wants the map.
  resultDocs: [rows, [], "results"],
  registrationContacts: [(snap) => {
    const out = {};
    snap.docs.forEach((d) => { out[d.id] = d.data(); });
    return out;
  }, {}],
};

export async function fetchCompParts(gymId, compId, parts, { sorted = false, tolerant = false } = {}) {
  const base = `gyms/${gymId}/competitions/${compId}`;
  for (const p of parts) {
    if (!PARTS[p]) throw new Error(`fetchCompParts: unknown part "${p}"`);
  }

  const settled = await Promise.all(parts.map(async (part) => {
    const [read, empty, sub = part] = PARTS[part];
    if (!tolerant) return [part, read(await getDocs(collection(db, base, sub)))];
    try {
      return [part, read(await getDocs(collection(db, base, sub)))];
    } catch {
      return [part, empty];
    }
  }));

  const out = Object.fromEntries(settled);
  if (sorted) {
    if (out.groups) out.groups = sortGroupsForDisplay(out.groups);
    if (out.sessions) out.sessions = sortSessionsByStartTime(out.sessions);
  }
  return out;
}

/**
 * The gym's competition list as a Firestore query — the one every full list
 * read of `gyms/{gymId}/competitions` goes through (docs/spec/05-competitions-core.md,
 * "Who can read a draft"). A reader that needs only the running ones filters
 * `status == "active"` instead, which the rule admits too.
 *
 * The rule lets anyone list an ANNOUNCED competition (`PUBLIC_COMPETITION_STATUSES`:
 * active, closing, closed) and only the gym-wide staff list a draft. For a list,
 * that rule is a constraint the QUERY must satisfy, not a filter Firestore
 * applies: an unfiltered query from a non-staff client is refused as a whole,
 * even in a gym with no draft at all. So the default carries the status filter,
 * and only a caller that knows the viewer holds `competitions.manage` (which
 * resolves the platform admin too — exactly the rule's two `list` terms) may
 * ask for the drafts.
 *
 * @param {string} gymId
 * @param {{ includeDrafts?: boolean, firestore?: object }} [opts]
 *   `includeDrafts`: true only for a `competitions.manage` holder.
 *   `firestore`: the instance to query, for a caller that is handed one (the
 *   personal data export); the app's own `db` otherwise.
 */
export function competitionsListQuery(gymId, { includeDrafts = false, firestore = db } = {}) {
  const col = collection(firestore, "gyms", gymId, "competitions");
  return includeDrafts ? col : query(col, where("status", "in", [...PUBLIC_COMPETITION_STATUSES]));
}
